// useWatchlistMeta — per-ticker aggregate for Watchlist v3.
//
// Pulls from existing v2 data hooks (no new endpoints) and joins them by
// ticker so List bar / Summary strip / Stock cards can read a uniform shape:
//
//   {
//     ticker, name, sector, one_liner,
//     verdict: 'BULLISH'|'BEARISH'|'NEUTRAL'|null,
//     change1d: number|null,
//     price: number|null,
//     earnings: { date, daysAway } | null,   // within next 14 days
//     freshSignals: number,                  // count of today's signal_events
//     alertLevel: 'urgent'|'warning'|'info'|'none'|null,
//   }
//
// Source mapping:
//   - useAtlasStocks   → name, sector, one_liner
//   - useStockUpdates  → thesis_status → verdict, change1d, alert_level
//   - useEarnings      → earnings_calendar within 14 days
//   - useSignalsToday  → freshSignals count (today's events only)
//
// Per-list new-signal dot needs to compare today's signals against each
// list's last_viewed_at — handled separately in ListBar so this hook stays
// O(N) over all tickers and doesn't re-render per list change.

import { useMemo } from 'react';
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks';
import { useStockUpdates, useEarnings, useSignalsToday } from '@/v2/hooks/useSupabaseData';

const THESIS_TO_VERDICT = {
  strengthening: 'BULLISH',
  weakening:     'BEARISH',
  unchanged:     'NEUTRAL',
};

function earningsDaysAway(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T00:00:00').getTime();
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00').getTime();
  return Math.round((target - today) / 86400000);
}

export function useWatchlistMeta() {
  const { stocks }   = useAtlasStocks();
  const { updates }  = useStockUpdates();
  const { earnings } = useEarnings();
  const { events }   = useSignalsToday();

  return useMemo(() => {
    const stockByTicker  = new Map(stocks.map(s => [s.ticker, s]));
    const updateByTicker = new Map();
    for (const u of updates) {
      // useStockUpdates returns multiple rows per day if rerun; keep latest.
      const cur = updateByTicker.get(u.ticker);
      if (!cur || (u.updated_at && (!cur.updated_at || u.updated_at > cur.updated_at))) {
        updateByTicker.set(u.ticker, u);
      }
    }
    const earningsByTicker = new Map();
    for (const e of earnings) {
      const days = earningsDaysAway(e.date);
      if (days != null && days >= 0 && days <= 14) {
        const cur = earningsByTicker.get(e.ticker);
        if (!cur || days < cur.daysAway) {
          earningsByTicker.set(e.ticker, { date: e.date, daysAway: days });
        }
      }
    }
    const freshCountByTicker = new Map();
    for (const ev of events) {
      freshCountByTicker.set(ev.ticker, (freshCountByTicker.get(ev.ticker) || 0) + 1);
    }

    return function metaFor(ticker) {
      const stock = stockByTicker.get(ticker) || null;
      const upd   = updateByTicker.get(ticker) || null;
      const earn  = earningsByTicker.get(ticker) || null;
      const fresh = freshCountByTicker.get(ticker) || 0;
      return {
        ticker,
        name:        stock?.name ?? ticker,
        sector:      stock?.sector ?? null,
        one_liner:   stock?.one_liner ?? null,
        // Legacy V1 rating string still lives on the stocks table (the
        // stock_updates.rating column was dropped 2026-05-09). Surface it so
        // v3 surfaces can render the ADR-0038 conviction vocab via
        // getRatingLabel without a second fetch.
        rating:      stock?.rating ?? null,
        verdict:     upd ? THESIS_TO_VERDICT[upd.thesis_status] ?? null : null,
        change1d:    typeof upd?.change1d === 'number' ? upd.change1d : null,
        price:       typeof upd?.price === 'number' ? upd.price : null,
        change5d:    typeof upd?.price_5d_change === 'number' ? upd.price_5d_change : null,
        earnings:    earn,
        freshSignals: fresh,
        alertLevel:  upd?.alert_level ?? null,
        conviction:  typeof upd?.conviction_delta === 'number' ? upd.conviction_delta : null,
        updatedAt:   upd?.updated_at ?? null,
      };
    };
  }, [stocks, updates, earnings, events]);
}

